import { EXPENSE_STATES, type ExpenseListQuery } from '@chukua/common';
import { Tag, type TableColumnsType } from 'antd';
import { Paperclip } from 'lucide-react';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import { formatMoney } from '../../../shared/policies/format-money';
import type { ExpenseCategoryView, ExpenseView } from '../expenses.api';
import { EXPENSE_STATE_LABELS, EXPENSE_STATE_TAG_COLOURS } from '../policies/expense-labels';

interface BuildExpenseColumnsArgs {
  list: Pick<ListQueryControls<ExpenseListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  categories: ExpenseCategoryView[];
}

/** Sorting and filtering run on the server: each column's `key` is its `sort` field or filter param in `expenseListQuerySchema`. */
export function buildExpenseColumns({ list, categories }: BuildExpenseColumnsArgs): TableColumnsType<ExpenseView> {
  return [
    {
      title: 'Date',
      key: 'deviceTime',
      sorter: true,
      sortOrder: list.sortOrderFor('deviceTime'),
      render: (_, expense) => new Date(expense.deviceTime).toLocaleString(),
    },
    { title: 'Driver', dataIndex: 'driverName', key: 'driver' },
    {
      title: 'For',
      key: 'subject',
      render: (_, expense) => (expense.truckRegistration ? `Truck ${expense.truckRegistration}` : 'A trip'),
    },
    {
      title: 'Category',
      key: 'category',
      sorter: true,
      sortOrder: list.sortOrderFor('category'),
      filters: categories.map((category) => ({ text: category.name, value: category.code })),
      filteredValue: list.filteredValueFor('category'),
      render: (_, expense) => expense.category.name,
    },
    {
      title: 'Amount',
      key: 'amount',
      align: 'right',
      sorter: true,
      sortOrder: list.sortOrderFor('amount'),
      render: (_, expense) => formatMoney(expense.amount),
    },
    {
      title: 'State',
      key: 'state',
      filters: EXPENSE_STATES.map((state) => ({ text: EXPENSE_STATE_LABELS[state], value: state })),
      filteredValue: list.filteredValueFor('state'),
      render: (_, expense) => <Tag color={EXPENSE_STATE_TAG_COLOURS[expense.state]}>{EXPENSE_STATE_LABELS[expense.state]}</Tag>,
    },
    {
      title: 'Receipts',
      key: 'receipts',
      render: (_, expense) => (
        <span className="inline-flex items-center gap-1 text-muted">
          <Paperclip className="size-4" aria-hidden />
          {expense.receipts.length}
        </span>
      ),
    },
  ];
}
